import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  Upload,
  Trash2,
  Pencil,
  Check,
  X,
  ImagePlus,
  GalleryHorizontal,
  GripVertical,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyItem = () => ({
  couple: "",
  location: "",
  year: "",
  date: "",
  palette: "",
  summary: "",
  images: [],
});

const GalleryEditor = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // item id being edited
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [rowDragId, setRowDragId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const headers = { "X-Admin-Token": token };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gallery`);
      setItems(res.data?.items || []);
    } catch {
      toast.error("Could not load gallery.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (item) => {
    setEditing(item.id);
    setDraft(JSON.parse(JSON.stringify(item)));
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft(null);
  };

  const onDraftField = (k, v) => setDraft({ ...draft, [k]: v });

  const onImageField = (idx, k, v) => {
    const images = [...draft.images];
    images[idx] = { ...images[idx], [k]: v };
    setDraft({ ...draft, images });
  };

  const removeImage = (idx) => {
    const images = draft.images.filter((_, i) => i !== idx);
    setDraft({ ...draft, images });
  };

  const reorderImages = (from, to) => {
    if (from === to || from < 0 || to < 0) return;
    const images = [...draft.images];
    const [moved] = images.splice(from, 1);
    images.splice(to, 0, moved);
    setDraft({ ...draft, images });
  };

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await axios.post(`${API}/admin/gallery/upload`, fd, {
      headers: { ...headers, "Content-Type": "multipart/form-data" },
    });
    return res.data?.url;
  };

  const uploadImages = async (files, replaceIdx = null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });

    try {
      if (replaceIdx !== null) {
        // Replace mode — only the first file is used.
        const url = await uploadOne(files[0]);
        if (!url) throw new Error("No URL returned");
        const images = [...draft.images];
        images[replaceIdx] = { ...images[replaceIdx], url };
        setDraft({ ...draft, images });
        setUploadProgress({ done: 1, total: 1 });
        toast.success("Image replaced.");
        return;
      }

      // Bulk add mode — parallelise but track progress.
      const newImages = [];
      let done = 0;
      await Promise.all(
        Array.from(files).map(async (file) => {
          try {
            const url = await uploadOne(file);
            if (url) {
              newImages.push({
                id: crypto.randomUUID
                  ? crypto.randomUUID()
                  : Math.random().toString(36).slice(2),
                label: "",
                url,
                span: "wide",
                _name: file.name,
              });
            }
          } catch (err) {
            toast.error(`Failed: ${file.name}`);
          } finally {
            done += 1;
            setUploadProgress({ done, total: files.length });
          }
        })
      );
      // Preserve original order of selected files (Promise.all is unordered for resolution).
      newImages.sort((a, b) => {
        const an = a._name || "";
        const bn = b._name || "";
        const ai = Array.from(files).findIndex((f) => f.name === an);
        const bi = Array.from(files).findIndex((f) => f.name === bn);
        return ai - bi;
      });
      newImages.forEach((n) => delete n._name);

      if (newImages.length > 0) {
        setDraft((d) => ({ ...d, images: [...(d?.images || []), ...newImages] }));
        toast.success(
          newImages.length === 1
            ? "Image uploaded."
            : `${newImages.length} images uploaded.`
        );
      }
    } catch (err) {
      toast.error("Upload failed. Try smaller images (under 12 MB each).");
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress({ done: 0, total: 0 }), 1200);
    }
  };

  const saveDraft = async () => {
    if (!draft.couple?.trim()) {
      toast.error("Couple name is required.");
      return;
    }
    setSaving(true);
    try {
      if (draft.id) {
        await axios.put(`${API}/admin/gallery/items/${draft.id}`, draft, {
          headers,
        });
      } else {
        await axios.post(`${API}/admin/gallery/items`, draft, { headers });
      }
      toast.success("Saved.");
      setEditing(null);
      setDraft(null);
      await load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not save. Please retry."
      );
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Delete this wedding entry? This cannot be undone."))
      return;
    try {
      await axios.delete(`${API}/admin/gallery/items/${id}`, { headers });
      toast.success("Deleted.");
      await load();
    } catch {
      toast.error("Could not delete. Try again.");
    }
  };

  const addNew = () => {
    setEditing("new");
    setDraft({ id: "", ...emptyItem() });
  };

  // Row-level drag-and-drop reordering (entire wedding entries).
  const onRowDragStart = (id) => setRowDragId(id);
  const onRowDragOver = (e) => e.preventDefault();
  const onRowDrop = async (targetId) => {
    if (!rowDragId || rowDragId === targetId) {
      setRowDragId(null);
      return;
    }
    const from = items.findIndex((it) => it.id === rowDragId);
    const to = items.findIndex((it) => it.id === targetId);
    if (from < 0 || to < 0) {
      setRowDragId(null);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
    setRowDragId(null);
    setReordering(true);
    try {
      await axios.post(
        `${API}/admin/gallery/reorder`,
        { order: next.map((it) => it.id) },
        { headers }
      );
      toast.success("Order saved.");
    } catch {
      toast.error("Could not save order. Reloading…");
      await load();
    } finally {
      setReordering(false);
    }
  };

  const isEditingNew = editing === "new";
  const isEditingExisting = (id) =>
    editing && editing !== "new" && editing === id;

  return (
    <section data-testid="gallery-editor" className="mt-16">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-3">Weddings · Gallery</div>
          <h2 className="font-serif text-3xl md:text-4xl font-light leading-tight">
            Edit the <span className="italic text-[#B8935C]">portfolio</span>.
          </h2>
          <p className="text-[#F0EBE1]/55 text-sm font-light mt-2 max-w-lg">
            Add, replace or remove wedding entries on the homepage portfolio.
            Drag the <GripVertical size={12} className="inline -mt-0.5" /> handle to
            reorder. Image uploads auto-compress to ~1600 px wide.
          </p>
        </div>
        <button
          onClick={addNew}
          data-testid="gallery-add-btn"
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#B8935C] text-[#080B12] text-[10px] uppercase tracking-[0.24em] font-semibold hover:bg-[#D4AF37] transition-colors"
        >
          <Plus size={14} /> New Wedding
        </button>
      </div>

      {loading && items.length === 0 ? (
        <div className="flex items-center text-[#F0EBE1]/45 py-16">
          <Loader2 size={18} className="animate-spin mr-3" /> Loading gallery…
        </div>
      ) : (
        <div className="space-y-5">
          {reordering && (
            <div className="flex items-center text-[#B8935C] text-[10px] uppercase tracking-[0.22em]">
              <Loader2 size={12} className="animate-spin mr-2" /> Saving order…
            </div>
          )}

          {isEditingNew && draft && (
            <ItemForm
              draft={draft}
              onChange={onDraftField}
              onImageField={onImageField}
              removeImage={removeImage}
              reorderImages={reorderImages}
              onUpload={uploadImages}
              onCancel={cancelEdit}
              onSave={saveDraft}
              saving={saving}
              uploading={uploading}
              uploadProgress={uploadProgress}
            />
          )}

          {items.map((item) =>
            isEditingExisting(item.id) ? (
              <ItemForm
                key={item.id}
                draft={draft}
                onChange={onDraftField}
                onImageField={onImageField}
                removeImage={removeImage}
                reorderImages={reorderImages}
                onUpload={uploadImages}
                onCancel={cancelEdit}
                onSave={saveDraft}
                saving={saving}
                uploading={uploading}
                uploadProgress={uploadProgress}
              />
            ) : (
              <ItemRow
                key={item.id}
                item={item}
                dragging={rowDragId === item.id}
                onDragStart={() => onRowDragStart(item.id)}
                onDragOver={onRowDragOver}
                onDrop={() => onRowDrop(item.id)}
                onEdit={() => startEdit(item)}
                onDelete={() => removeItem(item.id)}
              />
            )
          )}
        </div>
      )}
    </section>
  );
};

const ItemRow = ({
  item,
  dragging,
  onDragStart,
  onDragOver,
  onDrop,
  onEdit,
  onDelete,
}) => (
  <article
    data-testid={`gallery-row-${item.id}`}
    draggable
    onDragStart={onDragStart}
    onDragOver={onDragOver}
    onDrop={onDrop}
    className={`bg-[#121623] border p-5 md:p-6 flex flex-col md:flex-row gap-5 transition-all ${
      dragging
        ? "border-[#B8935C] opacity-50"
        : "border-[#F0EBE1]/10 hover:border-[#B8935C]/30"
    }`}
  >
    <div
      className="hidden md:flex items-center justify-center cursor-grab active:cursor-grabbing text-[#F0EBE1]/30 hover:text-[#B8935C] px-1"
      title="Drag to reorder"
      data-testid={`drag-handle-${item.id}`}
    >
      <GripVertical size={18} />
    </div>
    <div className="flex gap-2 flex-shrink-0">
      {item.images?.slice(0, 3).map((img) => (
        <img
          key={img.id || img.url}
          src={img.url}
          alt={img.label || item.couple}
          className="w-20 h-20 md:w-24 md:h-24 object-cover ring-1 ring-[#F0EBE1]/10"
          loading="lazy"
        />
      ))}
      {(!item.images || item.images.length === 0) && (
        <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-[#1A1F30] text-[#F0EBE1]/30 ring-1 ring-[#F0EBE1]/10">
          <GalleryHorizontal size={20} />
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <h3 className="font-serif text-2xl text-[#F0EBE1] leading-tight">
        {item.couple}
      </h3>
      {item.location && (
        <div className="overline text-[10px] mt-1 text-[#B8935C]">
          {item.location}
        </div>
      )}
      {item.date && (
        <div className="mt-2 text-xs text-[#F0EBE1]/55">{item.date}</div>
      )}
      {item.summary && (
        <p className="mt-2 text-[#F0EBE1]/65 text-sm line-clamp-2 font-light max-w-2xl">
          {item.summary}
        </p>
      )}
      {item.images?.length > 0 && (
        <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[#F0EBE1]/40">
          {item.images.length} {item.images.length === 1 ? "image" : "images"}
        </div>
      )}
    </div>
    <div className="flex gap-2 md:flex-col md:items-end">
      <button
        onClick={onEdit}
        data-testid={`edit-${item.id}`}
        className="inline-flex items-center gap-2 px-4 py-2 border border-[#F0EBE1]/15 text-[#F0EBE1]/80 text-[10px] uppercase tracking-[0.24em] hover:border-[#B8935C] hover:text-[#B8935C] transition-colors"
      >
        <Pencil size={12} /> Edit
      </button>
      <button
        onClick={onDelete}
        data-testid={`delete-${item.id}`}
        className="inline-flex items-center gap-2 px-4 py-2 border border-[#F0EBE1]/15 text-[#F0EBE1]/55 text-[10px] uppercase tracking-[0.24em] hover:border-[#6A1A25] hover:text-[#F0EBE1] hover:bg-[#6A1A25] transition-colors"
      >
        <Trash2 size={12} /> Delete
      </button>
    </div>
  </article>
);

const FieldRow = ({ label, children }) => (
  <div className="mb-4">
    <label className="overline block mb-1 text-[10px]">{label}</label>
    {children}
  </div>
);

const inputCls =
  "w-full bg-transparent border-0 border-b border-[#F0EBE1]/15 text-[#F0EBE1] font-light py-2 focus:outline-none focus:border-[#B8935C]";

const ItemForm = ({
  draft,
  onChange,
  onImageField,
  removeImage,
  reorderImages,
  onUpload,
  onCancel,
  onSave,
  saving,
  uploading,
  uploadProgress,
}) => {
  const [imgDragIdx, setImgDragIdx] = useState(null);
  const [dropZoneActive, setDropZoneActive] = useState(false);

  const onPickNew = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) onUpload(files, null);
    e.target.value = "";
  };

  const onPickReplace = (idx, e) => {
    const f = e.target.files?.[0];
    if (f) onUpload([f], idx);
    e.target.value = "";
  };

  const onDropZoneOver = (e) => {
    e.preventDefault();
    setDropZoneActive(true);
  };
  const onDropZoneLeave = () => setDropZoneActive(false);
  const onDropZoneDrop = (e) => {
    e.preventDefault();
    setDropZoneActive(false);
    const files = Array.from(e.dataTransfer?.files || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (files.length > 0) onUpload(files, null);
  };

  // Image drag handlers (in-form reorder).
  const onImgDragStart = (idx) => setImgDragIdx(idx);
  const onImgDragOver = (e) => e.preventDefault();
  const onImgDrop = (idx) => {
    if (imgDragIdx === null) return;
    reorderImages(imgDragIdx, idx);
    setImgDragIdx(null);
  };

  return (
    <article
      data-testid="gallery-form"
      className="bg-[#121623] border border-[#B8935C]/30 p-6 md:p-8"
    >
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#F0EBE1]/10">
        <div className="overline text-[10px]">
          {draft.id ? "Edit Wedding" : "New Wedding"}
        </div>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            data-testid="form-cancel"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#F0EBE1]/15 text-[#F0EBE1]/65 text-[10px] uppercase tracking-[0.24em] hover:border-[#F0EBE1]/40 hover:text-[#F0EBE1]"
          >
            <X size={12} /> Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving || uploading}
            data-testid="form-save"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#B8935C] text-[#080B12] text-[10px] uppercase tracking-[0.24em] font-semibold hover:bg-[#D4AF37] disabled:opacity-50"
          >
            {saving ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Check size={12} />
            )}
            Save
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-x-8">
        <FieldRow label="Couple *">
          <input
            value={draft.couple || ""}
            onChange={(e) => onChange("couple", e.target.value)}
            placeholder="Navya & Karanvir"
            className={inputCls}
            data-testid="field-couple"
          />
        </FieldRow>
        <FieldRow label="Venue / Location">
          <input
            value={draft.location || ""}
            onChange={(e) => onChange("location", e.target.value)}
            placeholder="Hyatt Regency Mansarovar · Jaipur"
            className={inputCls}
            data-testid="field-location"
          />
        </FieldRow>
        <FieldRow label="Year">
          <input
            value={draft.year || ""}
            onChange={(e) => onChange("year", e.target.value)}
            placeholder="2026"
            className={inputCls}
            data-testid="field-year"
          />
        </FieldRow>
        <FieldRow label="Date">
          <input
            value={draft.date || ""}
            onChange={(e) => onChange("date", e.target.value)}
            placeholder="18 – 20 February 2026"
            className={inputCls}
            data-testid="field-date"
          />
        </FieldRow>
        <FieldRow label="Palette">
          <input
            value={draft.palette || ""}
            onChange={(e) => onChange("palette", e.target.value)}
            placeholder="Crimson, saffron & ivory"
            className={inputCls}
            data-testid="field-palette"
          />
        </FieldRow>
        <div />
        <div className="md:col-span-2">
          <FieldRow label="Summary">
            <textarea
              value={draft.summary || ""}
              onChange={(e) => onChange("summary", e.target.value)}
              rows={3}
              placeholder="A short editorial description of the wedding…"
              className={inputCls + " resize-none"}
              data-testid="field-summary"
            />
          </FieldRow>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-[#F0EBE1]/10">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="overline text-[10px]">Images</div>
            <div className="text-[10px] text-[#F0EBE1]/45 mt-1">
              Drag <GripVertical size={10} className="inline -mt-0.5" /> to
              reorder. Select multiple files to upload at once.
            </div>
          </div>
          <label className="inline-flex items-center gap-2 px-4 py-2 border border-[#B8935C]/50 text-[#B8935C] text-[10px] uppercase tracking-[0.24em] cursor-pointer hover:bg-[#B8935C] hover:text-[#080B12] transition-colors">
            <Upload size={12} />
            {uploading
              ? uploadProgress.total > 1
                ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…`
                : "Uploading…"
              : "Add Images"}
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={onPickNew}
              className="hidden"
              data-testid="upload-new-image"
              disabled={uploading}
            />
          </label>
        </div>

        {uploading && uploadProgress.total > 0 && (
          <div className="mb-4">
            <div className="h-[2px] bg-[#F0EBE1]/10 overflow-hidden">
              <div
                className="h-full bg-[#B8935C] transition-all duration-300"
                style={{
                  width: `${(uploadProgress.done / uploadProgress.total) * 100}%`,
                }}
              />
            </div>
          </div>
        )}

        {draft.images?.length === 0 && (
          <div
            onDragOver={onDropZoneOver}
            onDragLeave={onDropZoneLeave}
            onDrop={onDropZoneDrop}
            className={`py-12 text-center border border-dashed transition-colors ${
              dropZoneActive
                ? "border-[#B8935C] bg-[#B8935C]/5 text-[#B8935C]"
                : "border-[#F0EBE1]/15 text-[#F0EBE1]/45"
            }`}
          >
            <ImagePlus size={24} className="mx-auto mb-2 opacity-60" />
            {dropZoneActive
              ? "Drop to upload"
              : "No images yet. Click 'Add Images' or drag files here."}
          </div>
        )}

        <div className="space-y-4">
          {draft.images?.map((img, idx) => (
            <div
              key={img.id || idx}
              draggable
              onDragStart={() => onImgDragStart(idx)}
              onDragOver={onImgDragOver}
              onDrop={() => onImgDrop(idx)}
              className={`grid grid-cols-12 gap-4 items-start bg-[#0B0F18] p-4 border transition-all ${
                imgDragIdx === idx
                  ? "border-[#B8935C] opacity-50"
                  : "border-[#F0EBE1]/8 hover:border-[#F0EBE1]/20"
              }`}
              data-testid={`image-row-${idx}`}
            >
              <div className="col-span-1 flex items-start justify-center pt-2">
                <div
                  className="cursor-grab active:cursor-grabbing text-[#F0EBE1]/30 hover:text-[#B8935C]"
                  title="Drag to reorder"
                >
                  <GripVertical size={16} />
                </div>
              </div>
              <div className="col-span-11 md:col-span-3">
                <img
                  src={img.url}
                  alt={img.label || draft.couple}
                  className="w-full h-32 md:h-28 object-cover ring-1 ring-[#F0EBE1]/10 pointer-events-none"
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <FieldRow label="Image Label / Caption">
                  <input
                    value={img.label || ""}
                    onChange={(e) =>
                      onImageField(idx, "label", e.target.value)
                    }
                    placeholder="Bridal Entry · Palace Doorway"
                    className={inputCls}
                  />
                </FieldRow>
                <div className="flex items-center gap-3 mt-2">
                  <span className="overline text-[10px]">Span:</span>
                  {["wide", "tall"].map((s) => (
                    <button
                      key={s}
                      onClick={() => onImageField(idx, "span", s)}
                      className={`px-3 py-1 text-[10px] uppercase tracking-[0.22em] border transition-colors ${
                        img.span === s
                          ? "border-[#B8935C] text-[#B8935C]"
                          : "border-[#F0EBE1]/15 text-[#F0EBE1]/55"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-span-12 md:col-span-2 flex md:flex-col gap-2 md:items-end">
                <label className="inline-flex items-center gap-2 px-3 py-2 border border-[#F0EBE1]/15 text-[#F0EBE1]/75 text-[9px] uppercase tracking-[0.22em] cursor-pointer hover:border-[#B8935C] hover:text-[#B8935C]">
                  <Upload size={11} /> Replace
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => onPickReplace(idx, e)}
                    className="hidden"
                  />
                </label>
                <button
                  onClick={() => removeImage(idx)}
                  className="inline-flex items-center justify-center w-8 h-8 border border-[#F0EBE1]/15 text-[#F0EBE1]/55 hover:border-[#6A1A25] hover:text-[#F0EBE1] hover:bg-[#6A1A25]"
                  aria-label="Delete image"
                  data-testid={`delete-image-${idx}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {draft.images?.length > 0 && (
          <div
            onDragOver={onDropZoneOver}
            onDragLeave={onDropZoneLeave}
            onDrop={onDropZoneDrop}
            className={`mt-4 py-6 text-center text-[10px] uppercase tracking-[0.22em] border border-dashed transition-colors ${
              dropZoneActive
                ? "border-[#B8935C] bg-[#B8935C]/5 text-[#B8935C]"
                : "border-[#F0EBE1]/10 text-[#F0EBE1]/35"
            }`}
          >
            {dropZoneActive
              ? "Drop to upload"
              : "Drag more images here to add"}
          </div>
        )}
      </div>
    </article>
  );
};

export default GalleryEditor;
